import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EnquiryForm } from "@/components/EnquiryForm";

/**
 * Global contact/enquiry modal. Any component can call
 *   const { open } = useContactModal();
 *   open({ propertyId, propertyTitle, agentId, agentName })
 * to overlay the enquiry form without leaving the current page.
 *
 * A document-level click interceptor also converts any <a href="/contact"> or
 * <Link to="/contact"> click into a modal open, parsing the standard
 * `?property_id=…&property_title=…&agent_id=…&agent_name=…` query params so
 * existing "Enquire" CTAs continue to work with no code changes.
 *
 * The `/contact` route stays functional for direct URL loads / external links.
 */
const ContactModalContext = createContext({ open: () => {}, close: () => {} });

export const ContactModalProvider = ({ children }) => {
  const [state, setState] = useState({ isOpen: false, ctx: {} });

  const open = useCallback((ctx = {}) => setState({ isOpen: true, ctx }), []);
  const close = useCallback(() => setState((s) => ({ ...s, isOpen: false })), []);

  // Intercept clicks on any anchor pointing at /contact and open the modal
  // instead of navigating. Skips modifier-key clicks, target="_blank", and
  // any anchor explicitly opting out via `data-no-modal`.
  useEffect(() => {
    const handler = (e) => {
      if (e.button !== 0) return; // only left click
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest("a");
      if (!anchor) return;
      if (anchor.hasAttribute("data-no-modal")) return;
      if (anchor.target && anchor.target !== "" && anchor.target !== "_self") return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      // Match /contact and /contact?... — ignore other paths.
      let pathname;
      let search = "";
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        pathname = url.pathname;
        search = url.search;
      } catch {
        return;
      }
      if (pathname !== "/contact") return;
      e.preventDefault();
      e.stopPropagation();
      const params = new URLSearchParams(search);
      open({
        propertyId: params.get("property_id") || undefined,
        propertyTitle: params.get("property_title") || undefined,
        agentId: params.get("agent_id") || undefined,
        agentName: params.get("agent_name") || undefined,
        agentLogo: params.get("agent_logo") || undefined,
      });
    };
    // Capture phase so we run BEFORE React Router's Link onClick handler,
    // which would otherwise call preventDefault and navigate.
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [open]);

  const { ctx } = state;
  const title = ctx.propertyTitle
    ? `Enquire About ${ctx.propertyTitle}`
    : "Send Us a Message";
  const description = ctx.propertyTitle
    ? "Send us your details and we'll be in touch about this property shortly."
    : "Fill in your details and we'll get back to you as soon as possible.";

  return (
    <ContactModalContext.Provider value={{ open, close }}>
      {children}
      <Dialog open={state.isOpen} onOpenChange={(v) => (v ? open(ctx) : close())}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto z-[310]"
          overlayClassName="z-[300]"
          data-testid="contact-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{title}</DialogTitle>
            <p className="text-sm text-muted-foreground">{description}</p>
          </DialogHeader>
          <EnquiryForm
            propertyId={ctx.propertyId}
            propertyTitle={ctx.propertyTitle}
            agentId={ctx.agentId}
            agentName={ctx.agentName}
            agentLogo={ctx.agentLogo}
            onSuccess={close}
          />
        </DialogContent>
      </Dialog>
    </ContactModalContext.Provider>
  );
};

export const useContactModal = () => useContext(ContactModalContext);
